import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  Send, Briefcase, Megaphone, HeadphonesIcon, BarChart3,
  Settings, ArrowLeft, Sparkles, Workflow, Globe,
  BadgeDollarSign, Calendar, Mail, MessageSquare,
  FileText, CreditCard, Database, Plug, Plus, Trash2,
  History, X, LogIn
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nativeModules = [
  { icon: Workflow, label: 'Moxey Flow', color: 'text-violet-600 bg-violet-50' },
];

const integrations = [
  { icon: Calendar, label: 'Calendar' },
  { icon: Mail, label: 'Email' },
  { icon: MessageSquare, label: 'Slack' },
  { icon: FileText, label: 'Google Docs' },
  { icon: CreditCard, label: 'QuickBooks' },
  { icon: Database, label: 'CRM' },
];

const suggestions = [
  'Get more leads',
  'Follow up with prospects',
  'Run a report',
  'Build a campaign',
];

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3" data-testid="moxey-typing">
      <div className="w-2 h-2 rounded-full bg-[#2563eb]/40 animate-bounce [animation-delay:0ms]" />
      <div className="w-2 h-2 rounded-full bg-[#2563eb]/40 animate-bounce [animation-delay:150ms]" />
      <div className="w-2 h-2 rounded-full bg-[#2563eb]/40 animate-bounce [animation-delay:300ms]" />
    </div>
  );
}

function MoxeyMessage({ text }) {
  const parts = text.split('\n');
  return (
    <div className="prose prose-sm max-w-none text-gray-700 font-body leading-relaxed">
      {parts.map((line, i) => {
        if (!line.trim()) return <br key={i} />;
        if (line.startsWith('- **')) {
          const match = line.match(/^- \*\*(.+?)\*\*\s*[---]?\s*(.*)/);
          if (match) return <p key={i} className="my-1"><span className="font-semibold text-gray-900">{match[1]}</span>{match[2] ? ` — ${match[2]}` : ''}</p>;
        }
        if (line.match(/^\d+\.\s\*\*/)) {
          const match = line.match(/^(\d+)\.\s\*\*(.+?)\*\*\s*[---]?\s*(.*)/);
          if (match) return <p key={i} className="my-1"><span className="text-[#2563eb] font-semibold">{match[1]}.</span> <span className="font-semibold text-gray-900">{match[2]}</span>{match[3] ? ` — ${match[3]}` : ''}</p>;
        }
        if (line.startsWith('## ')) return <p key={i} className="my-2 font-bold text-gray-900 text-base">{line.slice(3)}</p>;
        if (line.startsWith('### ')) return <p key={i} className="my-1.5 font-semibold text-gray-900">{line.slice(4)}</p>;
        if (line.startsWith('- ')) return <p key={i} className="my-1 pl-1">&#x2022; {line.slice(2)}</p>;
        return <p key={i} className="my-1">{line}</p>;
      })}
    </div>
  );
}

const WELCOME_MSG = {
  role: 'moxey',
  text: "Hi, I'm Moxey — your AI Business Leader. I can help you generate leads, follow up with prospects, build campaigns, track commissions, and more.\n\nJust tell me what you need. I'll handle the rest.",
};

export default function MoxeyApp() {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Load sessions list
  const loadSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${API}/chat/sessions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (e) { /* ignore */ }
  }, [isAuthenticated]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadSession = async (sid) => {
    try {
      const res = await fetch(`${API}/chat/history/${sid}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const loaded = data.messages.map(m => ({ role: m.role, text: m.text }));
        setMessages([WELCOME_MSG, ...loaded]);
        setSessionId(sid);
        setShowHistory(false);
      }
    } catch (e) { /* ignore */ }
  };

  const startNewChat = () => {
    setMessages([WELCOME_MSG]);
    setSessionId(null);
    setShowHistory(false);
  };

  const clearSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/chat/history/${sid}`, { method: 'DELETE', credentials: 'include' });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
    } catch (e) { /* ignore */ }
  };

  async function handleSend(text) {
    const msg = (text || input).trim();
    if (!msg || isTyping) return;

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setIsTyping(true);

    if (!isAuthenticated) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'moxey',
          text: "I'd love to help, but you need to be signed in to use Moxey AI. Create a free AREA account to get started!",
        }]);
        setIsTyping(false);
      }, 800);
      return;
    }

    try {
      const res = await fetch(`${API}/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: msg, session_id: sessionId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!sessionId) setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'moxey', text: data.response }]);
        loadSessions();
      } else {
        setMessages(prev => [...prev, {
          role: 'moxey',
          text: "Something went wrong. Please try again.",
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'moxey',
        text: "I'm having trouble connecting. Please check your connection and try again.",
      }]);
    }
    setIsTyping(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  return (
    <div className="h-screen flex flex-col bg-[#f5f6f8]" data-testid="moxey-app">

      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 sm:px-6 h-14 flex items-center justify-between flex-shrink-0 z-10" data-testid="moxey-app-header">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors mr-1" data-testid="moxey-app-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <img src="/images/moxey-avatar.png" alt="Moxey" className="w-8 h-8 rounded-full object-cover" />
            <div>
              <p className="text-sm font-bold text-gray-900 font-body leading-tight">Moxey</p>
              <p className="text-[11px] text-emerald-500 font-medium font-body leading-tight">AI-Powered</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAuthenticated && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="text-gray-500 hover:text-gray-800"
                data-testid="moxey-history-toggle"
              >
                <History className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline text-xs">History</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={startNewChat}
                className="text-gray-500 hover:text-gray-800"
                data-testid="moxey-new-chat"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline text-xs">New</span>
              </Button>
            </>
          )}
          <nav className="hidden sm:flex items-center gap-1">
            <Link to="/" className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 rounded-md hover:bg-gray-50 transition-colors font-body">Home</Link>
            <Link to="/moxey" className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 rounded-md hover:bg-gray-50 transition-colors font-body">Learn More</Link>
          </nav>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">

        {/* Session History Panel */}
        {showHistory && isAuthenticated && (
          <div className="w-64 border-r border-gray-100 bg-white flex flex-col flex-shrink-0" data-testid="moxey-sessions-panel">
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Conversations</span>
              <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {sessions.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No conversations yet</p>
              )}
              {sessions.map((s) => (
                <div
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                    sessionId === s.session_id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-600'
                  }`}
                  data-testid={`session-${s.session_id}`}
                >
                  <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="flex-1 truncate text-xs">{s.last_message}</span>
                  <button
                    onClick={(e) => clearSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6" data-testid="moxey-app-messages">
            <div className="max-w-2xl mx-auto space-y-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${i}`}>
                  {m.role === 'moxey' && (
                    <img src="/images/moxey-avatar.png" alt="Moxey" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1 mr-3" />
                  )}
                  <div className={`max-w-[85%] sm:max-w-[75%] ${
                    m.role === 'user'
                      ? 'rounded-2xl px-5 py-4 bg-[#2563eb] text-white shadow-sm'
                      : ''
                  }`}>
                    {m.role === 'user' ? (
                      <p className="text-sm font-body leading-relaxed">{m.text}</p>
                    ) : (
                      <div className="rounded-2xl px-5 py-4 bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)]">
                        <MoxeyMessage text={m.text} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <img src="/images/moxey-avatar.png" alt="Moxey" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1 mr-3" />
                  <div className="bg-white border border-gray-100 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)]">
                    <TypingIndicator />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Auth prompt for unauthenticated users */}
          {!isAuthenticated && (
            <div className="bg-blue-50 border-t border-blue-100 px-4 py-3 text-center" data-testid="moxey-auth-prompt">
              <p className="text-sm text-blue-700 mb-2">Sign in to get personalized AI-powered assistance</p>
              <div className="flex items-center justify-center gap-3">
                <Link to="/login">
                  <Button size="sm" variant="outline" className="text-xs" data-testid="moxey-login-btn">
                    <LogIn className="w-3 h-3 mr-1" /> Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="text-xs bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="moxey-register-btn">
                    Join Free
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Suggestions + Input */}
          <div className="border-t border-gray-100 bg-white px-4 sm:px-6 py-3 flex-shrink-0" data-testid="moxey-app-input-area">
            {messages.length <= 1 && (
              <div className="max-w-2xl mx-auto flex flex-wrap gap-2 mb-3" data-testid="moxey-app-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(s)}
                    className="px-4 py-2 text-xs font-medium text-[#2563eb] bg-blue-50 hover:bg-blue-100 rounded-full transition-colors font-body"
                    data-testid={`suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="max-w-2xl mx-auto flex items-center gap-3">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Moxey to run your business..."
                className="flex-1 h-12 px-5 rounded-2xl bg-[#f5f6f8] border border-gray-200 text-sm font-body text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]/40 transition-all"
                disabled={isTyping}
                data-testid="moxey-app-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || isTyping}
                className="h-12 w-12 rounded-2xl bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-sm disabled:opacity-40 flex-shrink-0"
                data-testid="moxey-app-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Side Panel (desktop) */}
        <aside className="hidden lg:flex flex-col w-60 border-l border-gray-100 bg-white px-5 py-6 flex-shrink-0 overflow-y-auto" data-testid="moxey-app-sidebar">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-body mb-3">Native Modules</p>
          <ul className="space-y-2.5 mb-6">
            {nativeModules.map((mod, i) => (
              <li key={i} className="flex items-center gap-3" data-testid={`sidebar-module-${i}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${mod.color}`}>
                  <mod.icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-gray-700 font-body">{mod.label}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-gray-100 pt-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-body mb-3">Connected</p>
            <ul className="space-y-2.5">
              {integrations.map((item, i) => (
                <li key={i} className="flex items-center gap-3" data-testid={`sidebar-integration-${i}`}>
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-500 font-body">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
